import { SETTINGS_FIELDS, CUSTOMIZATION_FIELDS, DEFAULT_VALUES, ECHO_WIDGET, WIDGETS_FUNCTIONS } from '../constants/WidgetConfig';
import { DEFAULT_PAGE_SIZE } from '../constants/App';
import * as API from '../services/API';

export const configContainsParam = (config, param) => Object.keys(config).includes(param);

export const buildDomainWithSuffix = (domain, propertyCode, channelId, suffix) => {
    if (!domain) {
        if (channelId) {
            return `${channelId.toLowerCase()}${suffix}`;
        }

        if (propertyCode) {
            return `${propertyCode.toLowerCase()}${suffix}`;
        }
    }

    return domain;
};

export const buildDomain = async (params) => {
    const { domain, propertyCode, channelId } = params;

    const ntpEnv = await API.getNtpEnvironment();
    setNtpEnv(ntpEnv);

    const suffix = ntpEnv === 'production' ? '.officialbookings.com' : '.dev.officialbookings.com';
    return buildDomainWithSuffix(domain, propertyCode, channelId, suffix);
};

export const buildInitialConfig = async (params) => {
    const result = { settings: {}, customizations: {} };
    const settingsKeys = Object.values(SETTINGS_FIELDS);
    const customizationsKeys = Object.values(CUSTOMIZATION_FIELDS);

    settingsKeys.forEach((settingKey) => {
        result.settings[settingKey] = configContainsParam(params, settingKey)
            ? params[settingKey]
            : null;
    });

    customizationsKeys.forEach((customizationKey) => {
        result.customizations[customizationKey] = configContainsParam(params, customizationKey)
            ? params[customizationKey]
            : null;
    });

    // Adds each param value that isn't in setting nor in customizations keys
    const isolatedParams = Object.keys(params).filter(param => !settingsKeys.includes(param) && !customizationsKeys.includes(param));
    isolatedParams.forEach((isolatedParam) => {
        result[isolatedParam] = params[isolatedParam];
    });

    if (!result.settings.domain) {
        result.settings.domain = await buildDomain(params);
    }

    return result;
};

const buildOffersParams = (params, searchData) => ({
    channelId    : params.settings.channelId,
    propertyCode : params.settings.propertyCode,
    checkIn      : params.settings.checkIn || searchData?.checkIn,
    checkOut     : params.settings.checkOut || searchData?.checkOut,
    currency     : (params.settings.currency && params.settings.currency.code) || 'EUR',
    occupancy    : params.settings.defaultOccupancy || {
        adults   : 1,
        children : [],
    },
    promotionalCode  : '',
    language         : params.settings.language,
    sortOrder        : params.settings.sortOrder,
    isRatesDisabled  : false,
    isOffersDisabled : false,
    sourceContext    : params.settings.searchId || undefined,
    rateFence        : '',
});

const buildEchoOffersParams = (params, searchData = null) => {
    const { settings } = params;
    const {
        propertyCode,
        checkIn, checkOut, language, channelId, searchId: sourceContext,
    } = settings;
    const clientPromoCode = (searchData && searchData.clientPromoCode) || '';
    return {
        channelId,
        propertyCode,
        sortOrder : 'PriceAsc',
        checkIn,
        checkOut,
        language,
        clientPromoCode,
        sourceContext,
        occupancy : params.settings.defaultOccupancy || {
            adults   : 1,
            children : [],
        },
    };
};

export const buildOffersRequestParams = (params, widgetType, searchData = null) => (widgetType === ECHO_WIDGET
    ? buildEchoOffersParams(params, searchData) : buildOffersParams(params, searchData));

export const buildHotelsListParams = (params, state) => {
    const { data: searchParams } = state.search;

    const { data: settingsData } = state.settings;

    const { filters, sortBy } = state.hotels;

    const { pageSize } = state.hotels.pagination || DEFAULT_PAGE_SIZE;

    const { currentPage : page } = state.hotels.pagination || 1;

    const currency = searchParams.currency && searchParams.currency.code && searchParams.currency.code !== 'pts'
        ? searchParams.currency.code
        : settingsData.currency.code;
    const promotionalCode = searchParams.clientPromoCode;
    const showUnavailable = params?.hotels?.showUnavailable;
    const distanceUnitOfMeasure = settingsData.autocomplete?.distanceUnitOfMeasure;

    return ({
        domain    : settingsData.domain,
        channelId : settingsData.channelId,
        checkIn   : searchParams.checkIn || DEFAULT_VALUES.checkIn,
        checkOut  : searchParams.checkOut || DEFAULT_VALUES.checkOut,
        occupancy : searchParams.occupancy.length > 0 ? searchParams.occupancy : [ { adults: 2, children: [] } ],
        currency,
        filters   : {
            ...filters,
            distanceUnitOfMeasure,
        },
        user: {
            language  : searchParams.language,
            userAgent : searchParams.userAgent,
            ipAddress : settingsData.ipAddress,
        },
        priceDisplayMode     : (params && params.priceDisplayMode) || 'stay',
        priceCalcMode        : (params && params.priceCalcMode) || 'asInPricelist',
        ...(sortBy !== '' && { sortCriteria: sortBy }),
        sourceContext        : settingsData.searchId || undefined,
        pageSize,
        page,
        promotionalCode,
        showUnavailable,
        coupon               : state.hotels.coupon || searchParams?.coupon,
        offersSortingDefault : settingsData.offersSortingDefault,
    });
};

export const disableRatesAndOffers = apiParams => ({
    ...apiParams,
    customizations: {
        ...apiParams.customizations,
        rates  : { isDisabled: true },
        offers : { isDisabled: true },
    },
});

/**
 * It checks what type of destination was selected at destinations filter (country, city or hotel), and
 * returns an object to filter by later
 *
 * @param {*} destination
 */
export const buildFiltersObject = (destination) => {
    if (!destination || (!destination.code && !destination.children)) {
        return {};
    }

    if (destination.value && !destination.code) {
        return {
            countryCode : destination.value,
            city        : '',
        };
    }

    return {
        countryCode : destination.code,
        city        : `${destination.value}`,
    };
};

export const filtersEmpty = (filters) => {
    const { countries } = filters;
    return !filters || (!countries[0]);
};

export const cityWithNoHotels = (customClassification, cityName) => ((cityName && !customClassification)
|| (cityName !== customClassification) || (customClassification === 'None'));

export const getDestinationType = (destination) => {
    if (destination.englishValue) {
        return 'country';
    }

    return 'hotel';
};

export const getCountryEnglishName = (regionOptions, code) => regionOptions.countries.find(country => country.value === code).englishValue;

/**
 * We may want to know what's in the config object for a given widget
 *
 * @param {string} widgetType widget type function used in DOM
 * @returns {object} The config present at .widget() function
 */
export const getInlineConfigFromQueue = (widgetType) => {
    const deepFoundArray = __KBE.q.find(deepArray => deepArray[0] === WIDGETS_FUNCTIONS[widgetType][0]
         || deepArray[0] === WIDGETS_FUNCTIONS[widgetType][1]);
    // Pre-condition: position 0 has the id, and the 1 has the config
    return deepFoundArray?.length ? deepFoundArray[1] : {};
};

let ntpEnv = null;

export function isProduction() {
    return ntpEnv === 'production';
}
export function getNtpEnv() {
    return ntpEnv;
}
function setNtpEnv(value) {
    ntpEnv = value;
}
